import { QRCodeSVG } from "qrcode.react"

import { comoSeLlama, type Empresa } from "@/componentes/empresa/tipos"
import { LogoSetieca } from "@/componentes/marca/LogoSetieca"

import { urlDeLaWeb } from "./contenido"
import { MARGEN, QR } from "./medidas"

/** El lema del aniversario: el mismo de la entrada del sistema y de la firma de correo. */
const PALABRA = "Años"
const FRASE = "Evolucionando la Logística"

/**
 * El pie del frente: el sello «25 Años · Evolucionando la Logística» con el nombre de
 * la agencia debajo, y el QR a la web.
 *
 * ⚠ **La marca va AQUÍ y no arriba.** Arriba estuvo el logotipo completo con la razón
 * social debajo; se quitó para subir la foto, que es lo que identifica a la persona, y
 * la marca bajó al pie en su versión del aniversario: el «25» con su lema a la derecha,
 * compuesto como en `IntroMarca` —cifra, unidad y debajo qué significa—, y el nombre de
 * la agencia debajo.
 *
 * ⚠ **El QR va en un recuadro BLANCO.** Sobre el vino un lector no encuentra el borde:
 * necesita un margen claro alrededor para saber dónde empieza el código. Y sin web en
 * «Empresa» no se pinta, ni el QR ni el recuadro vacío: lo que se dibuja dentro de la
 * pieza se imprime.
 */
export function PieCarnet({ empresa }: { empresa?: Empresa }) {
  const url = urlDeLaWeb(empresa)
  const agencia = empresa ? comoSeLlama(empresa) : ""

  return (
    <div
      style={{
        position: "absolute",
        left: `${MARGEN}mm`,
        right: `${MARGEN}mm`,
        bottom: "2.5mm",
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "space-between",
        gap: "2mm",
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: "1.2mm" }}>
          <LogoSetieca
            pieza="numero"
            style={{ flexShrink: 0, height: "7mm", width: "auto", color: "var(--impreso-papel)" }}
          />
          <div style={{ color: "var(--impreso-papel)" }}>
            <p style={{ margin: 0, fontSize: "2.8mm", fontWeight: 600, lineHeight: 1 }}>
              {PALABRA}
            </p>
            <p
              style={{
                margin: "0.5mm 0 0",
                fontSize: "1.7mm",
                fontWeight: 500,
                lineHeight: 1.1,
                whiteSpace: "nowrap",
                opacity: 0.92,
              }}
            >
              {FRASE}
            </p>
          </div>
        </div>
        {/* ⚠ `balance`: con el QR al lado no cabe en una línea, y envuelta a lo que
            caiga dejaba «C.A.» sola debajo. Equilibrada sale «SETIECA AGENTES /
            ADUANALES, C.A.». */}
        {agencia ? (
          <p
            style={{
              margin: "1mm 0 0",
              fontSize: "1.8mm",
              fontWeight: 600,
              letterSpacing: "0.15mm",
              lineHeight: 1.15,
              textTransform: "uppercase",
              textWrap: "balance",
              color: "var(--impreso-oro-3)",
            }}
          >
            {agencia}
          </p>
        ) : null}
      </div>

      {url ? (
        <div
          style={{
            flexShrink: 0,
            padding: "1.4mm",
            borderRadius: "1.2mm",
            background: "var(--impreso-papel)",
          }}
        >
          {/* Corrección M: el plástico se raya y refleja, y M recupera el 15 % de los
              módulos. El margen lo pone el recuadro, no el código. */}
          <QRCodeSVG
            value={url}
            level="M"
            marginSize={0}
            fgColor="var(--impreso-tinta)"
            bgColor="var(--impreso-papel)"
            title="Código QR a la web de la agencia"
            style={{ display: "block", width: `${QR}mm`, height: `${QR}mm` }}
          />
        </div>
      ) : null}
    </div>
  )
}
